import React, { useEffect, useState } from 'react'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

/**
 * Renders active subtitle/caption cues as a styled overlay on top of the video.
 *
 * HLS.js adds subtitles to the video element's TextTrack API. This component
 * hides native browser rendering via a <style> tag targeting ::cue, then reads
 * active cues from the text tracks and renders them with our own styling.
 */
export const HlsSubtitleOverlay: React.FC = () => {
  const { videoRef } = useHlsPlayerUI()
  const [cueText, setCueText] = useState<string[]>([])

  // Hide native ::cue rendering so only our overlay is visible.
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = '#hlsjs-player video::cue { opacity: 0; }'
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const getTextTracksArray = (): TextTrack[] => Array.from(video.textTracks)

    const onCueChange = () => {
      const activeCues: string[] = []
      for (const track of getTextTracksArray()) {
        if (track.mode === 'disabled' || !track.activeCues) continue
        for (const cue of Array.from(track.activeCues)) {
          if ('text' in cue && typeof cue.text === 'string') {
            activeCues.push(cue.text)
          }
        }
      }
      setCueText(activeCues)
    }

    const addTrackListeners = () => {
      for (const track of getTextTracksArray()) {
        track.addEventListener('cuechange', onCueChange)
      }
    }

    const removeTrackListeners = () => {
      for (const track of getTextTracksArray()) {
        track.removeEventListener('cuechange', onCueChange)
      }
    }

    // Re-attach listeners when tracks are added/removed/changed
    const onTrackChange = () => {
      removeTrackListeners()
      addTrackListeners()
      // Sync cues immediately in case active cues already exist
      onCueChange()
    }

    addTrackListeners()
    video.textTracks.addEventListener('addtrack', onTrackChange)
    video.textTracks.addEventListener('removetrack', onTrackChange)
    video.textTracks.addEventListener('change', onTrackChange)

    return () => {
      removeTrackListeners()
      video.textTracks.removeEventListener('addtrack', onTrackChange)
      video.textTracks.removeEventListener('removetrack', onTrackChange)
      video.textTracks.removeEventListener('change', onTrackChange)
      setCueText([])
    }
  }, [videoRef])

  if (cueText.length === 0) return null

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-20 flex flex-col items-center gap-1 px-4">
      {cueText.map((text, index) => (
        <span
          key={index}
          className="photon-heading-xl inline-block text-center font-medium text-white [text-shadow:_-1px_-1px_0_#000,_1px_-1px_0_#000,_-1px_1px_0_#000,_1px_1px_0_#000,_0_2px_4px_rgba(0,0,0,0.5)]"
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ))}
    </div>
  )
}
